import { useState, useContext, createContext } from 'react';

// Contexte pour la comparaison
const ComparisonContext = createContext();

export function useComparison() {
  const context = useContext(ComparisonContext);
  if (!context) {
    throw new Error('useComparison must be used within a ComparisonProvider');
  }
  return context;
}

export function ComparisonProvider({ children }) {
  const [selectedProducts, setSelectedProducts] = useState([]);
  const [maxProducts] = useState(6); // Augmenté à 6 produits

  // Ajouter un produit à la comparaison
  const addToComparison = (product) => {
    setSelectedProducts(prev => {
      // Vérifier si le produit est déjà dans la comparaison
      if (prev.find(p => p.id === product.id)) {
        return prev.filter(p => p.id !== product.id);
      }
      
      // Limiter au nombre maximum de produits
      if (prev.length >= maxProducts) {
        // Notification ou alerte pourrait être ajoutée ici
        console.log(`Maximum ${maxProducts} produits autorisés pour la comparaison`);
        return prev;
      }
      
      return [...prev, product];
    });
  };

  // Supprimer un produit de la comparaison
  const removeFromComparison = (productId) => {
    setSelectedProducts(prev => prev.filter(p => p.id !== productId));
  };

  // Vider la comparaison
  const clearComparison = () => {
    setSelectedProducts([]);
  };

  // Vérifier si un produit est sélectionné
  const isSelected = (productId) => {
    return selectedProducts.some(p => p.id === productId);
  };

  // Compter les produits sélectionnés
  const getCount = () => {
    return selectedProducts.length;
  };

  // Vérifier si on peut ajouter plus de produits
  const canAddMore = () => {
    return selectedProducts.length < maxProducts;
  };

  // Obtenir le nombre de produits restants qu'on peut ajouter
  const getRemainingSlots = () => {
    return maxProducts - selectedProducts.length;
  };

  // Réorganiser les produits (drag and drop)
  const reorderProducts = (fromIndex, toIndex) => {
    const newProducts = [...selectedProducts];
    const [movedProduct] = newProducts.splice(fromIndex, 1);
    newProducts.splice(toIndex, 0, movedProduct);
    setSelectedProducts(newProducts);
  };

  // Sélectionner plusieurs produits à la fois
  const addMultipleToComparison = (products) => {
    setSelectedProducts(prev => {
      const newProducts = [...prev];
      const availableSlots = maxProducts - prev.length;
      
      products.forEach(product => {
        if (!newProducts.find(p => p.id === product.id) && newProducts.length < maxProducts) {
          newProducts.push(product);
        }
      });
      
      return newProducts;
    });
  };

  return (
    <ComparisonContext.Provider value={{
      selectedProducts,
      maxProducts,
      addToComparison,
      removeFromComparison,
      clearComparison,
      isSelected,
      getCount,
      canAddMore,
      getRemainingSlots,
      reorderProducts,
      addMultipleToComparison
    }}>
      {children}
    </ComparisonContext.Provider>
  );
}
